import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { formatMessage, intlLocale, type MessageBundle } from './format-message';
import { setStaticTranslator } from './static-translate';
import { LOCALES, LOCALE_META, PSEUDO_LOCALE, isLocale, matchLocale, pseudoize, type Locale, type ShippedLocale } from './locales';

export { LOCALES };

/** Every catalogue, filled as each language loads (English eagerly: it is every language's last fallback). */
export const messages = Object.fromEntries([...LOCALES, PSEUDO_LOCALE].map((l) => [l, {}])) as Record<Locale, MessageBundle>;

/*
 * W11: one JSON catalogue per language (`catalogues/<locale>.json`), loaded when the language is first used. The
 * pseudo-locale is computed from English.
 */
const loaders: Record<ShippedLocale, () => Promise<MessageBundle>> = {
  en: () => import('./catalogues/en.json').then((m) => m.default as MessageBundle),
  tr: () => import('./catalogues/tr.json').then((m) => m.default as MessageBundle),
  de: () => import('./catalogues/de.json').then((m) => m.default as MessageBundle),
  fr: () => import('./catalogues/fr.json').then((m) => m.default as MessageBundle),
  es: () => import('./catalogues/es.json').then((m) => m.default as MessageBundle),
  it: () => import('./catalogues/it.json').then((m) => m.default as MessageBundle),
  nl: () => import('./catalogues/nl.json').then((m) => m.default as MessageBundle),
  pl: () => import('./catalogues/pl.json').then((m) => m.default as MessageBundle),
  pt: () => import('./catalogues/pt.json').then((m) => m.default as MessageBundle),
  ru: () => import('./catalogues/ru.json').then((m) => m.default as MessageBundle),
  uk: () => import('./catalogues/uk.json').then((m) => m.default as MessageBundle),
  ar: () => import('./catalogues/ar.json').then((m) => m.default as MessageBundle),
  'zh-Hans': () => import('./catalogues/zh-Hans.json').then((m) => m.default as MessageBundle),
  ja: () => import('./catalogues/ja.json').then((m) => m.default as MessageBundle),
};
const loading: Partial<Record<Locale, Promise<void>>> = {};
const loaded = new Set<Locale>();
const listeners = new Set<() => void>();

export function loadLocale(locale: Locale): Promise<void> {
  if (locale === PSEUDO_LOCALE) return loadLocale('en');
  loading[locale] ??= loaders[locale]().then((bundle) => {
    Object.assign(messages[locale], bundle);
    loaded.add(locale);
    for (const listener of listeners) listener();
  });
  return loading[locale];
}

/** W12: the tenant's own wording per language (Settings › Localization), laid over the shipped catalogue. */
const overrides: Partial<Record<Locale, MessageBundle>> = {};

export function setTranslationOverrides(locale: Locale, bundle: MessageBundle): void {
  overrides[locale] = bundle;
  for (const listener of listeners) listener();
}

/**
 * W2 (spec §4.4, ADR-0502 §9): terminology overlays — the wording an installed pack or app lays over generic keys
 * ("HS code" → "GTİP" under `tr.customs`). They sit between the shipped catalogue and the tenant's own wording, which
 * still wins. `overlays.ts` loads them; this only holds the merged bundle per language.
 */
const overlayBundles: Partial<Record<Locale, MessageBundle>> = {};

export function setOverlayBundle(locale: Locale, bundle: MessageBundle): void {
  overlayBundles[locale] = bundle;
  for (const listener of listeners) listener();
}

export function isLocaleLoaded(locale: Locale): boolean {
  return locale === PSEUDO_LOCALE ? loaded.has('en') : loaded.has(locale);
}

export type Translate = (key: string, params?: Record<string, unknown>) => string;

interface I18nContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  bundles: Record<Locale, MessageBundle>;
  fallbackLocale?: Locale;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export { isLocale };

/** The browser's first language the product ships, else English. */
export function detectLocale(): Locale {
  if (typeof navigator === 'undefined') return 'en';
  const tags = [...(navigator.languages ?? []), navigator.language];
  for (const tag of tags) {
    const match = matchLocale(tag);
    if (match) return match;
  }
  return 'en';
}

/**
 * The fallback chain (spec W10): the viewer's language › the tenant's default › English › the key itself. The
 * pseudo-locale renders English through `pseudoize`.
 */
export function translate(bundles: Record<Locale, MessageBundle>, locale: Locale, key: string, params?: Record<string, unknown>, fallbackLocale?: Locale): string {
  if (locale === PSEUDO_LOCALE) {
    const english = bundles.en?.[key];
    return english === undefined ? key : formatMessage(pseudoize(english), params, 'en');
  }
  const pattern = bundles[locale]?.[key] ?? (fallbackLocale ? bundles[fallbackLocale]?.[key] : undefined) ?? bundles.en?.[key];
  if (pattern === undefined) return key;
  return formatMessage(pattern, params, locale);
}

export interface I18nProviderProps {
  children: ReactNode;
  locale?: Locale;
  /** Extra messages merged over the defaults per locale. */
  bundles?: Partial<Record<Locale, MessageBundle>>;
  onLocaleChange?: (l: Locale) => void;
  /** The tenant's default language: the second step of the fallback chain. */
  fallbackLocale?: Locale;
}

export function I18nProvider({ children, locale: controlledLocale, bundles: extraBundles, onLocaleChange, fallbackLocale }: I18nProviderProps): ReactElement {
  const [locale, setLocaleState] = useState<Locale>(() => controlledLocale ?? detectLocale());
  // Re-render when a catalogue arrives (a language chosen for the first time, or a render before the bootstrap load).
  const [catalogueVersion, setCatalogueVersion] = useState(0);
  useEffect(() => {
    const listener = () => setCatalogueVersion((v) => v + 1);
    listeners.add(listener);
    void loadLocale('en');
    void loadLocale(locale);
    if (fallbackLocale) void loadLocale(fallbackLocale);
    return () => void listeners.delete(listener);
  }, [locale, fallbackLocale]);
  // Follow the `locale` prop whenever it changes (it remains switchable through `setLocale` in between).
  const [syncedLocale, setSyncedLocale] = useState(controlledLocale);
  if (controlledLocale !== syncedLocale) {
    setSyncedLocale(controlledLocale);
    if (controlledLocale && controlledLocale !== locale) setLocaleState(controlledLocale);
  }

  const merged = useMemo<Record<Locale, MessageBundle>>(
    () =>
      Object.fromEntries(
        Object.entries(messages).map(([l, bundle]) => [l, { ...bundle, ...(extraBundles?.[l as Locale] ?? {}), ...(overlayBundles[l as Locale] ?? {}), ...(overrides[l as Locale] ?? {}) }]),
      ) as Record<Locale, MessageBundle>,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [extraBundles, catalogueVersion],
  );

  const setLocale = useCallback(
    (next: Locale) => {
      if (!isLocale(next)) return;
      setLocaleState(next);
      onLocaleChange?.(next);
      if (typeof document !== 'undefined') {
        document.documentElement.lang = LOCALE_META[next].intl;
        document.documentElement.dir = LOCALE_META[next].dir;
      }
    },
    [onLocaleChange],
  );

  useEffect(() => {
    // W11: the page speaks the viewer's language and runs in its direction (Arabic right to left).
    if (typeof document !== 'undefined') {
      document.documentElement.lang = LOCALE_META[locale].intl;
      document.documentElement.dir = LOCALE_META[locale].dir;
    }
  }, [locale]);

  const value = useMemo<I18nContextValue>(() => ({ locale, setLocale, bundles: merged, fallbackLocale }), [locale, setLocale, merged, fallbackLocale]);
  // W12: codes and server labels humanized outside React read the same catalogue.
  setStaticTranslator((key) => {
    const text = translate(merged, locale, key, undefined, fallbackLocale);
    return text === key ? undefined : text;
  });
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

function useI18nContext(): I18nContextValue {
  const ctx = useContext(I18nContext);
  return ctx ?? { locale: 'en', setLocale: () => undefined, bundles: messages };
}

/** Translate function bound to the current locale; falls back to `en`, then to the key. */
export function useT(): Translate {
  const { locale, bundles, fallbackLocale } = useI18nContext();
  return useCallback((key: string, params?: Record<string, unknown>) => translate(bundles, locale, key, params, fallbackLocale), [bundles, locale, fallbackLocale]);
}

export function useLocale(): { locale: Locale; setLocale: (l: Locale) => void; locales: readonly ShippedLocale[]; fallbackLocale?: Locale } {
  const { locale, setLocale, fallbackLocale } = useI18nContext();
  return { locale, setLocale, locales: LOCALES, fallbackLocale };
}

export interface UseI18nResult {
  t: Translate;
  locale: Locale;
  setLocale: (l: Locale) => void;
  formatNumber: (n: number, opts?: Intl.NumberFormatOptions) => string;
  formatDate: (d: Date | string | number, opts?: Intl.DateTimeFormatOptions) => string;
  formatDateTime: (d: Date | string | number) => string;
  formatRelative: (d: Date | string | number, now?: Date | number) => string;
}

function toDate(d: Date | string | number): Date {
  return d instanceof Date ? d : new Date(d);
}

/** Human relative time (`in 3 hours`, `2 days ago`) using Intl.RelativeTimeFormat. */
export function formatRelativeTime(locale: Locale, d: Date | string | number, now: Date | number = Date.now()): string {
  const target = toDate(d).getTime();
  const base = now instanceof Date ? now.getTime() : now;
  if (Number.isNaN(target)) return '';
  const diffSeconds = Math.round((target - base) / 1000);
  const rtf = new Intl.RelativeTimeFormat(intlLocale(locale), { numeric: 'auto' });
  const abs = Math.abs(diffSeconds);
  if (abs < 45) return rtf.format(diffSeconds, 'second');
  if (abs < 45 * 60) return rtf.format(Math.round(diffSeconds / 60), 'minute');
  if (abs < 22 * 3600) return rtf.format(Math.round(diffSeconds / 3600), 'hour');
  if (abs < 26 * 86400) return rtf.format(Math.round(diffSeconds / 86400), 'day');
  if (abs < 320 * 86400) return rtf.format(Math.round(diffSeconds / (30 * 86400)), 'month');
  return rtf.format(Math.round(diffSeconds / (365 * 86400)), 'year');
}

export function useI18n(): UseI18nResult {
  const t = useT();
  const { locale, setLocale } = useI18nContext();
  const tag = intlLocale(locale);
  return useMemo<UseI18nResult>(
    () => ({
      t,
      locale,
      setLocale,
      formatNumber: (n, opts) => new Intl.NumberFormat(tag, opts).format(n),
      formatDate: (d, opts) => new Intl.DateTimeFormat(tag, opts ?? { dateStyle: 'medium' }).format(toDate(d)),
      formatDateTime: (d) => new Intl.DateTimeFormat(tag, { dateStyle: 'medium', timeStyle: 'short' }).format(toDate(d)),
      formatRelative: (d, now) => formatRelativeTime(locale, d, now),
    }),
    [t, locale, setLocale, tag],
  );
}
